import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Award, FileDown, FileStack, ListOrdered, Mail } from "lucide-react";
import { subjectColor } from "@/lib/constants";

export default function Reports() {
  const [params] = useSearchParams();
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [results, setResults] = useState([]);
  const [resultId, setResultId] = useState(params.get("result") || "");
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [topicData, setTopicData] = useState(null);

  useEffect(() => { api.get("/exams").then((r) => setExams(r.data)); api.get("/settings").then((r) => setSettings(r.data)); }, []);
  useEffect(() => { if (examId) api.get(`/exams/${examId}/results`).then((r) => setResults(r.data)); }, [examId]);
  useEffect(() => {
    if (resultId) api.get(`/results/${resultId}`).then((r) => { setReport(r.data); setExamId(r.data.exam_id); }).catch((e) => toast.error(apiError(e)));
  }, [resultId]);
  useEffect(() => {
    if (report?.id) api.get(`/results/${report.id}/topic-analysis`).then((r) => setTopicData(r.data)).catch(() => setTopicData(null));
    else setTopicData(null);
  }, [report?.id]);

  const subjects = useMemo(() => report ? report.per_subject : [], [report]);
  const weakTopics = useMemo(() => (topicData?.topics || []).filter((t) => t.wrong > 0), [topicData]);
  const strongTopics = useMemo(() => (topicData?.topics || []).filter((t) => t.wrong === 0 && t.correct > 0), [topicData]);
  const classList = useMemo(() => {
    const set = new Set();
    results.forEach((r) => set.add(`${r.grade}-${r.branch}`));
    return [...set].sort();
  }, [results]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [waClass, setWaClass] = useState("");
  const [wrongData, setWrongData] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waOpen, setWaOpen] = useState({});
  const loadWrongAnalysis = async (cls) => {
    setWaClass(cls); setWaLoading(true); setWaOpen({}); setWrongData(null);
    const i = cls.lastIndexOf("-"); const g = cls.slice(0, i); const b = cls.slice(i + 1);
    try {
      const { data } = await api.get(`/exams/${examId}/wrong-analysis`, { params: { grade: g, branch: b } });
      setWrongData(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setWaLoading(false); }
  };
  const sendParentEmail = async () => {
    setSendingEmail(true);
    try { const { data } = await api.post(`/results/${report.id}/send-parent-email`); toast.success(`Karne veliye e-posta ile gönderildi: ${data.to}`); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSendingEmail(false); }
  };

  return (
    <div>
      <div className="no-print">
        <PageHeader title="Raporlar & Karneler" desc="Öğrenci bireysel sınav karnesi (A4, yazdırmaya hazır)">
          {examId && <Button variant="outline" onClick={async () => { try { await downloadFile(`/exams/${examId}/net-list.pdf`, "net_sirali_liste.pdf"); toast.success("Net Sıralı Liste PDF indirildi"); } catch (e) { toast.error(apiError(e)); } }} data-testid="rep-netlist-pdf"><ListOrdered className="h-4 w-4 mr-2" />Net Liste (Tümü)</Button>}
          {examId && classList.length > 0 && (
            <Select onValueChange={async (cls) => { const i = cls.lastIndexOf("-"); const g = cls.slice(0, i); const b = cls.slice(i + 1); try { await downloadFile(`/exams/${examId}/net-list.pdf?grade=${encodeURIComponent(g)}&branch=${encodeURIComponent(b)}`, `net_${cls}.pdf`); toast.success(`${cls} net/puan listesi indirildi`); } catch (e) { toast.error(apiError(e)); } }}>
              <SelectTrigger className="w-48" data-testid="rep-class-netlist"><SelectValue placeholder="Sınıf Net/Puan PDF" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {classList.map((c) => <SelectItem key={c} value={c}>{c} — Net/Puan Listesi</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {examId && classList.length > 0 && (
            <Select onValueChange={async (cls) => { const i = cls.lastIndexOf("-"); const g = cls.slice(0, i); const b = cls.slice(i + 1); try { await downloadFile(`/exams/${examId}/karnes.pdf?grade=${encodeURIComponent(g)}&branch=${encodeURIComponent(b)}`, `karneler_${cls}.pdf`); toast.success(`${cls} sınıf karneleri indirildi`); } catch (e) { toast.error(apiError(e)); } }}>
              <SelectTrigger className="w-52" data-testid="rep-class-karne"><SelectValue placeholder="Sınıf Karneleri (PDF)" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {classList.map((c) => <SelectItem key={c} value={c}>{c} — Tüm Karneler</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {examId && <Button variant="outline" onClick={async () => { try { await downloadFile(`/exams/${examId}/results.pdf`, "toplu_sonuclar.pdf"); toast.success("Toplu sonuç PDF indirildi"); } catch (e) { toast.error(apiError(e)); } }} data-testid="rep-bulk-pdf"><FileStack className="h-4 w-4 mr-2" />Toplu Sonuç PDF</Button>}
          {report && <Button variant="outline" onClick={sendParentEmail} disabled={sendingEmail || !report.parent_email} title={report.parent_email ? report.parent_email : "Veli e-postası tanımlı değil"} data-testid="rep-send-email"><Mail className="h-4 w-4 mr-2" />{sendingEmail ? "Gönderiliyor..." : "Veliye E-posta Gönder"}</Button>}
          {report && <Button variant="outline" onClick={async () => { try { await downloadFile(`/results/${report.id}/karne.pdf`, `karne_${report.number}.pdf`); toast.success("Karne PDF indirildi"); } catch (e) { toast.error(apiError(e)); } }} data-testid="rep-karne-pdf"><FileDown className="h-4 w-4 mr-2" />Karne PDF İndir</Button>}
          {report && <Button onClick={() => window.print()} data-testid="report-print"><Printer className="h-4 w-4 mr-2" />Yazdır / PDF</Button>}
        </PageHeader>
        <Card className="p-5 bg-card border-border mb-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Sınav</Label>
              <Select value={examId} onValueChange={(v) => { setExamId(v); setResultId(""); setReport(null); }}><SelectTrigger data-testid="rep-exam"><SelectValue placeholder="Sınav seçin" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Öğrenci</Label>
              <Select value={resultId} onValueChange={setResultId}><SelectTrigger data-testid="rep-result"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">{results.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.student_name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        </Card>
        {examId && classList.length > 0 && (
          <Card className="p-5 bg-card border-border mb-6" data-testid="wrong-analysis-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <div className="text-base font-bold">Detaylı Yanlış Analizi</div>
                <div className="text-xs text-muted-foreground">Sınıf bazlı: hangi öğrenci, hangi dersten, hangi soruyu ve konuyu yanlış yapmış.</div>
              </div>
              <Select value={waClass} onValueChange={loadWrongAnalysis}>
                <SelectTrigger className="w-52" data-testid="wa-class-select"><SelectValue placeholder="Sınıf seçin" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {classList.map((c) => <SelectItem key={c} value={c}>{c} Sınıfı</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {waLoading && <div className="text-sm text-muted-foreground py-4">Yükleniyor...</div>}
            {!waLoading && wrongData && (
              wrongData.students.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4">Bu sınıfta sonuç bulunamadı.</div>
              ) : (
                <div className="space-y-3">
                  {wrongData.students.map((st, si) => (
                    <div key={si} className="rounded-lg border border-border overflow-hidden" data-testid={`wa-student-${st.number}`}>
                      <button onClick={() => setWaOpen((o) => ({ ...o, [si]: !o[si] }))} className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted transition-colors text-left">
                        <span className="font-semibold text-sm">{st.number} · {st.student_name}</span>
                        <span className="flex items-center gap-3 text-xs">
                          <span className="text-muted-foreground">Net: <b className="text-foreground">{st.total_net}</b> · Puan: <b className="text-foreground">{st.score}</b></span>
                          <span className={`px-2 py-0.5 rounded-full font-semibold ${st.total_wrong > 0 ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"}`}>{st.total_wrong} yanlış</span>
                        </span>
                      </button>
                      {waOpen[si] && (
                        st.wrongs.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-emerald-700 dark:text-emerald-400">Bu öğrenci hiç yanlış yapmamış. 🎉</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-background border-b border-border text-left">
                                  <th className="px-3 py-2 font-semibold">Soru</th>
                                  <th className="px-3 py-2 font-semibold">Ders</th>
                                  <th className="px-3 py-2 font-semibold">Konu</th>
                                  <th className="px-3 py-2 font-semibold">Kazanım</th>
                                  <th className="px-3 py-2 font-semibold text-center">Doğru</th>
                                  <th className="px-3 py-2 font-semibold text-center">Öğrencinin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {st.wrongs.map((w, wi) => {
                                  const c = subjectColor(w.subject_key);
                                  return (
                                    <tr key={wi} className="border-b border-border/60">
                                      <td className="px-3 py-1.5 font-mono">{w.q}</td>
                                      <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${c.badge}`}>{w.subject}</span></td>
                                      <td className="px-3 py-1.5">{w.topic}</td>
                                      <td className="px-3 py-1.5 text-muted-foreground">{w.kazanim || "—"}</td>
                                      <td className="px-3 py-1.5 text-center font-bold text-emerald-600">{w.correct}</td>
                                      <td className="px-3 py-1.5 text-center font-bold text-red-600">{w.given}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </Card>
        )}
      </div>

      {!report ? <div className="no-print"><EmptyState icon={Award} title="Karne seçin" desc="Sınav ve öğrenci seçerek bireysel karneyi görüntüleyin." /></div> : (
        <div className="print-area">
          <div className="bg-white text-black mx-auto max-w-[800px] p-10 rounded-xl shadow-xl border border-border" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div className="flex items-center justify-between border-b-2 border-black pb-4">
              <div className="flex items-center gap-3">
                {settings?.logo_url ? <img src={settings.logo_url} alt="logo" className="h-14 w-14 object-contain" /> : null}
                <div>
                  <div className="text-sm font-bold">{settings?.institution_name?.toUpperCase()}</div>
                  <div className="text-xl font-extrabold">ÖĞRENCİ KARNESİ — {report.exam_name}</div>
                  <div className="text-xs text-gray-600 mt-1">{report.exam_date}</div>
                </div>
              </div>
              <Award className="h-12 w-12 text-black" />
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mt-4">
              <div><b>Ad Soyad:</b> {report.student_name}</div>
              <div><b>Okul No:</b> {report.number}</div>
              <div><b>Sınıf:</b> {report.grade}-{report.branch}</div>
              <div><b>Veli:</b> {report.parent_name || "—"}</div>
            </div>

            <table className="w-full mt-6 text-sm border-collapse">
              <thead><tr className="bg-gray-100 text-left">
                <th className="p-2 border border-gray-300">Ders</th><th className="p-2 border border-gray-300 text-center">Kat</th><th className="p-2 border border-gray-300 text-center">Soru</th>
                <th className="p-2 border border-gray-300 text-center">Doğru</th><th className="p-2 border border-gray-300 text-center">Yanlış</th>
                <th className="p-2 border border-gray-300 text-center">Boş</th>
                <th className="p-2 border border-gray-300 text-center">Öğrenci Net</th>
                <th className="p-2 border border-gray-300 text-center">Sınıf Ort.</th>
                <th className="p-2 border border-gray-300 text-center">Durum</th>
              </tr></thead>
              <tbody>
                {subjects.map((s) => {
                  const cavg = (report.class_subject_avg || {})[s.key] ?? 0;
                  const diff = Math.round((s.net - cavg) * 100) / 100;
                  const above = diff >= 0;
                  return (
                    <tr key={s.key}>
                      <td className="p-2 border border-gray-300 font-medium">{s.name}</td>
                      <td className="p-2 border border-gray-300 text-center font-bold text-teal-700">×{s.weight ?? 1}</td>
                      <td className="p-2 border border-gray-300 text-center">{s.total}</td>
                      <td className="p-2 border border-gray-300 text-center text-emerald-700 font-semibold">{s.correct}</td>
                      <td className="p-2 border border-gray-300 text-center text-red-700 font-semibold">{s.wrong}</td>
                      <td className="p-2 border border-gray-300 text-center text-gray-500">{s.blank}</td>
                      <td className="p-2 border border-gray-300 text-center font-bold text-base">{s.net}</td>
                      <td className="p-2 border border-gray-300 text-center text-gray-600">{cavg}</td>
                      <td className="p-2 border border-gray-300 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${above ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {above ? "▲" : "▼"} {above ? "+" : ""}{diff}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-bold">
                  <td className="p-2 border border-gray-300">TOPLAM</td>
                  <td className="p-2 border border-gray-300"></td>
                  <td className="p-2 border border-gray-300 text-center">{report.total_questions}</td>
                  <td className="p-2 border border-gray-300 text-center text-emerald-700">{report.total_correct}</td>
                  <td className="p-2 border border-gray-300 text-center text-red-700">{report.total_wrong}</td>
                  <td className="p-2 border border-gray-300 text-center text-gray-500">{report.total_blank}</td>
                  <td className="p-2 border border-gray-300 text-center text-base">{report.total_net}</td>
                  <td className="p-2 border border-gray-300 text-center text-gray-600">{report.class_average_net}</td>
                  <td className="p-2 border border-gray-300"></td>
                </tr>
              </tbody>
            </table>

            <div className="mt-2 text-xs text-gray-500">"Durum" sütunu, öğrencinin o dersteki netinin sınıf ortalamasına göre farkını gösterir (▲ üstünde, ▼ altında).</div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              <div className="border-2 border-teal-700 rounded-lg p-3 text-center"><div className="text-xs text-gray-600">ÖĞRENCİ NET</div><div className="text-2xl font-extrabold text-teal-800">{report.total_net}</div></div>
              <div className="border-2 border-teal-700 rounded-lg p-3 text-center"><div className="text-xs text-gray-600">PUAN</div><div className="text-2xl font-extrabold text-teal-800">{report.score}</div></div>
              <div className="border-2 border-gray-300 rounded-lg p-3 text-center"><div className="text-xs text-gray-600">SINIF ORT. NET</div><div className="text-2xl font-extrabold text-gray-700">{report.class_average_net}</div></div>
              <div className="border-2 border-gray-300 rounded-lg p-3 text-center"><div className="text-xs text-gray-600">SINIF SIRASI</div><div className="text-2xl font-extrabold text-gray-700">{report.class_rank ? `${report.class_rank}/${report.class_size}` : "—"}</div></div>
            </div>

            {report.parent_message && (
              <div className="mt-6 border-2 border-teal-600 rounded-lg p-4 bg-teal-50/60" data-testid="karne-parent-message">
                <div className="font-extrabold text-teal-800 text-sm mb-1">SAYIN VELİ</div>
                <p className="text-sm text-gray-800 leading-relaxed">{report.parent_message}</p>
              </div>
            )}

            {topicData && topicData.has_questions && strongTopics.length > 0 && (
              <div className="mt-6 border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50/50" data-testid="karne-strong-topics">
                <div className="font-extrabold text-emerald-800 text-sm mb-1">TEBRİKLER! GÜÇLÜ VE TAM KAVRANAN KONULAR</div>
                <p className="text-xs text-gray-600 mb-3">Öğrencimiz aşağıdaki konularda <b>hiç yanlış yapmadı</b> — kendisini tebrik edebilirsiniz:</p>
                <div className="space-y-1.5">
                  {strongTopics.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`strong-topic-${i}`}>
                      <span className="font-bold text-emerald-900 shrink-0">{t.subject_name}</span>
                      <span className="text-emerald-300">›</span>
                      <span className="flex-1 font-semibold text-emerald-900">{t.topic}{t.kazanim ? <span className="text-xs text-emerald-600 font-mono ml-1">[{t.kazanim}]</span> : null}</span>
                      <span className="text-xs font-bold text-emerald-700 shrink-0">{t.correct}/{t.total} doğru</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 shrink-0">Tam Başarı</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {topicData && topicData.has_questions && (
              <div className="mt-6 border-2 border-red-200 rounded-lg p-4 bg-red-50/40" data-testid="karne-weak-topics">
                <div className="font-extrabold text-red-800 text-sm mb-1">VELİ İÇİN GELİŞİM REHBERİ: TEKRAR EDİLMESİ GEREKEN KONULAR</div>
                <p className="text-xs text-gray-600 mb-3">
                  Bu bölümde, öğrencinin sınavda <b>yanlış yaptığı soruların ait olduğu konular</b> listelenmektedir.
                  {weakTopics.length > 0 && ` Öğrencimizin ${report.total_wrong} yanlışı aşağıdaki konularda yoğunlaşmaktadır.`}
                </p>
                {weakTopics.length === 0 ? (
                  <p className="text-sm text-emerald-700 font-semibold">Tebrikler! Konu ataması yapılmış sorularda yanlış bulunmuyor.</p>
                ) : (
                  <div className="space-y-1.5">
                    {weakTopics.map((t, i) => {
                      const sev = t.wrong >= 3 ? { label: "Öncelikli tekrar", cls: "bg-red-100 text-red-800 border-red-300" }
                        : t.wrong === 2 ? { label: "Tekrar edilmeli", cls: "bg-amber-100 text-amber-800 border-amber-300" }
                        : { label: "Gözden geçirilmeli", cls: "bg-gray-100 text-gray-700 border-gray-300" };
                      return (
                        <div key={i} className="flex items-center gap-2 text-sm" data-testid={`weak-topic-${i}`}>
                          <span className="font-semibold text-gray-800 shrink-0">{t.subject_name}</span>
                          <span className="text-gray-400">›</span>
                          <span className="flex-1">{t.topic}{t.kazanim ? <span className="text-xs text-gray-500 font-mono ml-1">[{t.kazanim}]</span> : null}</span>
                          <span className="text-xs font-bold text-red-700 shrink-0">{t.wrong} yanlış</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${sev.cls}`}>{sev.label}</span>
                        </div>
                      );
                    })}
                    <p className="text-xs text-gray-500 pt-2">Öneri: "Öncelikli tekrar" işaretli konulardan başlayarak konu anlatımı tekrarı ve ek soru çözümü yapılması önerilir.</p>
                  </div>
                )}
              </div>
            )}
            {topicData && !topicData.has_questions && (
              <div className="mt-6 border border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-500" data-testid="karne-weak-empty">
                Konu bazlı eksik analizi için sınavın Cevap Anahtarı → "Konular (2026 Müfredat)" sekmesinden sorulara konu atayın.
              </div>
            )}

            <div className="flex justify-between mt-16 text-sm">
              <div className="text-center"><div className="border-t border-black w-48 pt-1">{report.advisor ? <><span className="font-semibold">{report.advisor}</span><div className="text-xs text-gray-500">Etüt Hocası</div></> : "Etüt Hocası"}</div></div>
              <div className="text-center"><div className="border-t border-black w-40 pt-1">Veli İmza</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
